import { useTranslation } from 'react-i18next'
import type { ProductChannelListing, ProductOption } from './types'

export type OptionSyncTone = 'success' | 'warning' | 'danger' | 'neutral'

export interface OptionSyncState {
  tone: OptionSyncTone
  label: string
  detail: string | null
}

type Translate = ReturnType<typeof useTranslation>['t']

function formatShortDate(value: string | null | undefined, locale: string): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

/** The Salla listing that links this option, if any. */
export function optionListing(option: ProductOption): ProductChannelListing | null {
  return option.channelListings.find(listing => listing.externalVariantId) ?? option.channelListings[0] ?? null
}

function observedQuantity(listing: ProductChannelListing): number | null {
  const finite = listing.stocks.filter(stock => !stock.infinite && stock.quantity != null)
  if (finite.length === 0) return listing.finiteStockQuantity ?? null
  return finite.reduce((sum, stock) => sum + (stock.quantity ?? 0), 0)
}

function submittedQuantity(listing: ProductChannelListing): number | null {
  const submitted = listing.stocks.filter(stock => stock.submittedQuantity != null)
  if (submitted.length === 0) return null
  return submitted.reduce((sum, stock) => sum + (stock.submittedQuantity ?? 0), 0)
}

/**
 * What Salla shows for an option compared with Tlaaq's available stock. With Tlaaq as the source a
 * matching submitted quantity counts as in sync; otherwise we report what Salla last showed.
 */
export function optionSyncState(option: ProductOption, tlaaqIsSource: boolean, t: Translate, locale: string): OptionSyncState | null {
  const listing = optionListing(option)
  if (!listing) return null
  const observed = observedQuantity(listing)
  const submitted = submittedQuantity(listing)
  const pushedAt = formatShortDate(listing.lastStockPushedAt, locale)
  const pulledAt = formatShortDate(listing.lastSeenAt, locale)
  if (!tlaaqIsSource) {
    return {
      tone: 'neutral',
      label: t('pages.products.options.sync.matchesSalla'),
      detail: pulledAt ? t('pages.products.options.sync.pulledAt', { date: pulledAt }) : null,
    }
  }
  if (option.status !== 'ACTIVE') {
    return {
      tone: 'warning',
      label: t('pages.products.options.sync.blocked', { status: t(`pages.products.status.${option.status}`) }),
      detail: null,
    }
  }
  const quantity = submitted ?? observed
  if (quantity != null && quantity === option.availableQty) {
    return {
      tone: 'success',
      label: t('pages.products.options.sync.inSync', { quantity: quantity.toLocaleString(locale) }),
      detail: pushedAt ? t('pages.products.options.sync.pushedAt', { date: pushedAt }) : null,
    }
  }
  return {
    tone: 'neutral',
    label: observed == null
      ? t('pages.products.options.sync.notPushed')
      : t('pages.products.options.sync.sallaShows', { quantity: observed.toLocaleString(locale) }),
    detail: t('pages.products.options.sync.willPush', { quantity: option.availableQty.toLocaleString(locale) }),
  }
}
